import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@frontend/api/client';
import type { ISODate } from '@shared/types';

export interface LeaveTypeOption {
  value: string;
  label: string;
  metadata?: Record<string, unknown>;
  daysPerYear?: number;
  unlimited?: boolean;
  accrualBased?: boolean;
  carryOverMaxDays?: number;
  maxConsecutiveDays?: number;
  allowHalfDay?: boolean;
  allowNegativeBalance?: boolean;
}

export interface LeaveTypesResponse {
  eligible?: boolean;
  employmentType?: string;
  leaveYear?: string;
  workWeek?: string[];
  holidayCalendar?: string;
  minNoticeDays?: number;
  secondLevelAboveDays?: number;
  types: LeaveTypeOption[];
}

export interface LeaveBalanceView {
  leaveType: string;
  year: number;
  entitledDays: string;
  accruedDays: string;
  carriedOverDays: string;
  usedDays: string;
  pendingDays: string;
  availableDays: string;
  unlimited?: boolean;
  encashedDays?: string;
  expiredDays?: string;
}

export interface LeaveRequestView {
  id: string;
  employeeId: string;
  leaveType: string;
  fromDate: ISODate;
  toDate: ISODate;
  days: string;
  status: string;
  isHalfDay: boolean;
  halfDayPeriod: string | null;
  reason?: string | null;
  submittedAt?: string | null;
  approverId?: string | null;
  approvedAt?: string | null;
  rejectionReason?: string | null;
  workflowInstanceId?: string | null;
}

export interface MyLeaveResponse {
  year: number;
  employeeId: string;
  eligible: boolean;
  requests: LeaveRequestView[];
  balances: LeaveBalanceView[];
}

export function useLeaveTypes(): UseQueryResult<LeaveTypesResponse> {
  return useQuery({
    queryKey: ['leave', 'types'],
    queryFn: () => api.get<LeaveTypesResponse>('/leaves/types'),
    staleTime: 10 * 60_000,
  });
}

export function useMyLeave(year?: number): UseQueryResult<MyLeaveResponse> {
  return useQuery({
    queryKey: ['leave', 'mine', year ?? 'current'],
    queryFn: () => api.get<MyLeaveResponse>('/leaves/my', { year }),
  });
}

export interface LeavePreview {
  days: string;
  breakdown: { date: ISODate; isWorkingDay: boolean; reason?: string }[];
  calendarDays: number;
  workingDates: ISODate[];
  availableDays: string;
  afterDays: string | null;
  unlimited: boolean;
  needsSecondApproval: boolean;
}

export interface PreviewInput {
  fromDate: ISODate;
  toDate: ISODate;
  leaveType: string;
  isHalfDay?: boolean;
  halfDayPeriod?: string | null;
}

/**
 * The live "what will this cost me" calculation.
 *
 * The request form calls this on every date change rather than computing days locally,
 * because weekends, public holidays and half-day rules are POLICY, resolved per employee
 * at runtime. A client-side day count would be a second, divergent implementation of the
 * leave calendar — and would be wrong for anyone whose work week is not Mon–Fri.
 */
export function useLeavePreview(input: PreviewInput | null): UseQueryResult<LeavePreview> {
  return useQuery({
    queryKey: ['leave', 'preview', input],
    queryFn: () => api.post<LeavePreview>('/leaves/preview', input),
    enabled:
      input !== null && input.fromDate !== '' && input.toDate !== '' && input.leaveType !== '' && input.fromDate <= input.toDate,
    retry: false,
    staleTime: 0,
  });
}

export interface SubmitLeaveInput {
  leaveType: string;
  fromDate: ISODate;
  toDate: ISODate;
  reason?: string | null;
  isHalfDay?: boolean;
  halfDayPeriod?: string | null;
  contactDuringLeave?: string | null;
}

export function useSubmitLeave(): ReturnType<
  typeof useMutation<{ request?: LeaveRequestView; workflowInstanceId?: string | null }, unknown, SubmitLeaveInput>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitLeaveInput) =>
      api.post<{ request?: LeaveRequestView; workflowInstanceId?: string | null }>('/leaves', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leave'] });
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function useWithdrawLeave(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch(`/leaves/${id}/withdraw`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function usePendingLeave(): UseQueryResult<{ requests: LeaveRequestView[] }> {
  return useQuery({
    queryKey: ['leave', 'pending'],
    queryFn: () => api.get<{ requests: LeaveRequestView[] }>('/leaves/pending'),
    retry: false,
  });
}

export function useLeaveBalances(employeeId: string | null, year?: number): UseQueryResult<{
  employeeId: string;
  year: number;
  eligible: boolean;
  balances: LeaveBalanceView[];
}> {
  return useQuery({
    queryKey: ['leave', 'balances', employeeId, year ?? 'current'],
    queryFn: () =>
      api.get<{ employeeId: string; year: number; eligible: boolean; balances: LeaveBalanceView[] }>(
        `/leaves/balances/${employeeId ?? ''}`,
        { year },
      ),
    enabled: employeeId !== null && employeeId !== '',
  });
}

/**
 * ONE LEDGER MOVEMENT, named the way the server names it.
 *
 * This was previously declared as `{ entryType, deltaDays, balanceAfter, description }` —
 * four fields the route has never returned. Nothing threw, because `humanise(undefined)`
 * answers with an em dash, so every movement rendered as A DATE, AN EM DASH AND NOTHING
 * ELSE: no amount, no running balance, no explanation. The screen built specifically so a
 * disputed balance never needs a DBA answered with a list of dates.
 */
export interface LedgerEntry {
  id: string;
  effectiveDate: ISODate;
  /** ACCRUAL, GRANT, DEDUCTION, CARRY_OVER, EXPIRY, ADJUSTMENT… */
  kind: string;
  /** Signed. Negative is a deduction. */
  days: string;
  /** Machine code for the movement's source, e.g. `LEAVE_APPROVED`. */
  code: string;
  /** The human explanation. Written by the server; safe verbatim. */
  text: string;
  /** The running balance immediately after this movement. */
  balanceAfterDays: string;
  leaveRequestId?: string | null;
}

/**
 * `replayedFromLedger` is a set of BUCKETS, not a balance view — it has no `availableDays`
 * field, and the `!reconciles` banner used to read exactly that. Available is derived from
 * the buckets by the invariant the server uses: entitled + carried over + adjustments +
 * comp-off − used − pending. `availableFromBuckets` below is that invariant, and it is why
 * the one moment this screen most needs to work does not render "undefined days".
 */
export interface LedgerBuckets {
  entitledDays: string;
  carriedOverDays: string;
  adjustmentDays: string;
  usedDays: string;
  pendingDays: string;
  compOffDays: string;
}

export interface LedgerExplanation {
  balance: LeaveBalanceView;
  buckets: LedgerBuckets;
  replayedFromLedger: LedgerBuckets;
  /** False means the cached balance disagrees with a replay of the ledger. */
  reconciles: boolean;
  entries: LedgerEntry[];
}

/** The available-days invariant, applied to a bucket set. Mirrors `availableOf` server-side. */
export function availableFromBuckets(buckets: LedgerBuckets): string {
  const number = (value: string | undefined): number => {
    const parsed = Number(value ?? '0');
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const available =
    number(buckets.entitledDays) +
    number(buckets.carriedOverDays) +
    number(buckets.adjustmentDays) +
    number(buckets.compOffDays) -
    number(buckets.usedDays) -
    number(buckets.pendingDays);
  return available.toFixed(2);
}

export function useLeaveLedger(
  employeeId: string | null,
  leaveType: string | null,
  year?: number,
): UseQueryResult<LedgerExplanation> {
  return useQuery({
    queryKey: ['leave', 'ledger', employeeId, leaveType, year ?? 'current'],
    queryFn: () =>
      api.get<LedgerExplanation>(`/leaves/balances/${employeeId ?? ''}/ledger`, {
        leaveType: leaveType ?? '',
        year,
      }),
    enabled: employeeId !== null && leaveType !== null && leaveType !== '',
  });
}

export interface CalendarDay {
  date: ISODate;
  employeeId: string;
  employeeName?: string;
  leaveType: string;
  status: string;
  isHalfDay?: boolean;
}

export interface LeaveCalendarResponse {
  from: ISODate;
  to: ISODate;
  days?: CalendarDay[];
  entries?: CalendarDay[];
  holidays?: { date: ISODate; name: string }[];
}

export function useTeamCalendar(from: ISODate, to: ISODate): UseQueryResult<LeaveCalendarResponse> {
  return useQuery({
    queryKey: ['leave', 'team-calendar', from, to],
    queryFn: () => api.get<LeaveCalendarResponse>('/leaves/team-calendar', { from, to }),
    retry: false,
  });
}

export function useMyLeaveCalendar(from: ISODate, to: ISODate): UseQueryResult<LeaveCalendarResponse> {
  return useQuery({
    queryKey: ['leave', 'my-calendar', from, to],
    queryFn: () => api.get<LeaveCalendarResponse>('/leaves/my/calendar', { from, to }),
  });
}

export interface Holiday {
  id: string;
  date: ISODate;
  name: string;
  type: string;
  country?: string;
  state?: string | null;
  isOptional?: boolean;
}

export function useHolidays(year?: number): UseQueryResult<{ holidays: Holiday[] }> {
  return useQuery({
    queryKey: ['leave', 'holidays', year ?? 'current'],
    queryFn: () => api.get<{ holidays: Holiday[] }>('/leaves/holidays', { year }),
    staleTime: 30 * 60_000,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Holiday calendar
//
// All three routes had no caller, so the company calendar could be READ and never built.
// That is not a cosmetic gap: leave day counts exclude weekends and configured holidays, and
// with none configured the only exclusions are the eleven US federal holidays. Every company
// closure the federal calendar does not cover — the Friday after Thanksgiving, Christmas Eve —
// was silently deducted from people's balances, with no way for an administrator to fix it.
// ─────────────────────────────────────────────────────────────────────────────

export type HolidayType = 'NATIONAL' | 'REGIONAL' | 'COMPANY' | 'OPTIONAL';

export interface HolidayInput {
  name: string;
  date: ISODate;
  type?: HolidayType;
  /** US state codes, when the day is observed regionally rather than company-wide. */
  states?: string[];
  /** A day the company works that would otherwise be a holiday. */
  isWorkingDay?: boolean;
  description?: string | null;
}

function invalidateHolidays(queryClient: ReturnType<typeof useQueryClient>): void {
  // The calendar is cached for thirty minutes and feeds the leave PREVIEW, so a stale list
  // would keep quoting the old day count after an administrator fixed it.
  void queryClient.invalidateQueries({ queryKey: ['leave'] });
}

export function useCreateHoliday(): ReturnType<typeof useMutation<unknown, unknown, HolidayInput>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: HolidayInput) => api.post('/leaves/holidays', body),
    onSuccess: () => invalidateHolidays(queryClient),
  });
}

export function useUpdateHoliday(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string } & Partial<HolidayInput>>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<HolidayInput>) =>
      api.patch(`/leaves/holidays/${id}`, patch),
    onSuccess: () => invalidateHolidays(queryClient),
  });
}

export function useDeleteHoliday(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/leaves/holidays/${id}`),
    onSuccess: () => invalidateHolidays(queryClient),
  });
}
